/** EDITAREA PARCAJULUI, pe pânza vizualizatorului.
 *
 *  Subsolul se editează PE DESENUL LUI, nu pe masa de tronsoane: masa e un
 *  model de pereți și camere, iar un loc de parcare ar fi acolo o clasă
 *  străină cu reguli proprii. Pânza de parcaj desenează deja locurile, aleile,
 *  stâlpii, rampele și adăposturile, cu zoom, panoramare și selecție — deci
 *  editorul e un MOD al ei: «Editează parcajul» intră, uneltele schimbă ce fac
 *  apăsarea și clicul, «Închide editarea» iese. Ce seamănă cu editorul de
 *  tronsoane e interfața, nu codul: bara uneltelor, bara selecției, ⌘Z/⇧⌘Z,
 *  Esc, Delete, garda «ai schimbări nesalvate» (`nescris`), «Salvează».
 *
 *  CE ȚINE: copia de lucru a LOCURILOR și a ALEILOR planșeului (în cadrul
 *  desenului), într-o singură stare cu un singur istoric — un ⌘Z întoarce
 *  ultimul gest, oricare ar fi fost —, unealta, ce e ales, tragerea în curs,
 *  fantoma locului nou. CE NU: regulile (`model.ts`) și traducerea în document
 *  (`scriere.ts`).
 *
 *  CUM SE LEAGĂ DE PÂNZĂ: `Vizualizator` îi dă întâi lui fiecare apăsare,
 *  mișcare și ridicare; dacă editorul le ia (trage un loc, un mâner), panoramarea
 *  nu pornește. Un clic (apăsare care nu s-a mutat) vine prin `clic`, cu ținta
 *  lui — același `data-pick` prin care pânza își află selecția. Locurile și
 *  aleile de lucru iau locul celor din document în `PlanView`, iar `strat` se
 *  desenează peste planșă: conflictele în culoare, mânerele, fantoma.
 *
 *  CONFLICTELE se calculează doar pe locurile ATINSE (mutate, noi, sau prinse
 *  de o alee lungită), față de toate celelalte și de obstacolele nivelului:
 *  cele neatinse le-a așezat generatorul, iar o verificare a tuturor la fiecare
 *  cadru de tragere ar fi pătratică. Salvarea REFUZĂ un loc care stă peste ceva
 *  (Oana, 22.10.2026: suprapunerile sunt erori de desen); «nedeservit» e doar
 *  avertisment.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent,
         type ReactNode } from "react";
import * as api from "../../api";
import { t as tr, useT } from "../../i18n";
import { tineNescrisul } from "../../nescris";
import type { CanonicalDoc, CanonicalObject, StoreyPlan } from "../canonical";
import type { SolvedPlan } from "../client";
import { selectionFromTarget, type Selection } from "../inspect";
import { idCanonic, inCadrulDocumentului } from "../plan/nivelTronson";
import type { Pt } from "../types";
import { inelLoc, Istoric, conflicte as conflicteLe, lipeste, locNou, muta, pas, roteste,
         rotesteLa, unghiulDinManer, pozitiaManerului, mutaCapatul, lungimeaAleii,
         rotesteAleea, manerulAleii, mijloculAleii,
         pregatesteObstacole, PAS_SAGEATA, PAS_SAGEATA_MARE,
         axaTragerii, PRAG_AXA, GOL_DESERVIT, ADANCIME_PROBA, coteVecinatate,
         type Alee, type Axa, type Conflict, type Loc, type Obstacol, type Obstacole } from "./model";
import { inelAlee, seSuprapun, unghiulAleii } from "./geometrie";
import { aleiCaObiecte, aleiSterse, caObiecte, diferenta, diferentaAlei } from "./scriere";
import Cota, { COTA_INT } from "../plan/Cota";

export type Unealta = "sageata" | "nou";

export type OptiuniEditorParcaj = {
  /** E modul de editare pornit (butonul apăsat și planșeul editabil)? */
  activ: boolean;
  /** Planșa de parcaj a planșeului editat, în cadrul desenului. */
  plan: SolvedPlan | null;
  /** Planșeul, cu conturul, pereții, stâlpii, camerele și rampele lui. */
  nivel: StoreyPlan | null;
  /** Documentul în cadrul desenului (`docLocal`) și originea scăzută din el. */
  doc: CanonicalDoc | null;
  origine: readonly [number, number];
  projectId: string;
  solutionId: string;
  potScrie: boolean;
  /** Punctul de sub cursor, în metrii desenului. */
  punctul: (e: { clientX: number; clientY: number }) => { x: number; y: number } | null;
  /** Scara curentă a desenului, ca mânerele să aibă mărime fixă pe ecran. */
  pxPerM: number;
  /** După o scriere reușită: vizualizatorul recitește documentul. */
  laSalvat: () => void;
  /** «Închide editarea». */
  laInchide: () => void;
};

/** Ce e pe masă: locurile și aleile planșeului, în cadrul desenului. */
type Stare = { locuri: Loc[]; alei: Alee[] };
const GOALA: Stare = { locuri: [], alei: [] };

/** Un gest în curs pe pânză: tragerea unui loc (`muta`), a mânerului lui de
 *  rotire (`roteste`) sau a unui capăt de alee (`capat`). */
type Tragere =
  | { fel: "muta" | "roteste"; id: string; ultim: Pt; miscat: boolean; liber: boolean;
      /** La mutare, TOATE locurile alese merg cu cel tras. */
      ids: string[];
      /** De unde a pornit și pe ce axă a locului alunecă — hotărâtă o dată,
       *  după `PRAG_AXA` de drum, ca să nu sară între axe pe parcurs. */
      start: Pt; axa: Axa | null }
  | { fel: "capat"; id: string; capat: 0 | 1; ultim: Pt; miscat: boolean }
  | { fel: "rotesteAlee"; id: string; ultim: Pt; miscat: boolean };

/** Cât se așteaptă recitirea documentului după o scriere, până se renunță. */
const RECITIRE_MAX_MS = 60_000;

/** Mânerul de rotire: la atâtea px dincolo de fața locului, cu raza asta. */
const MANER_PX = 18, MANER_RAZA_PX = 6;

/** Obstacolele fixe ale nivelului, ca inele: ce a așezat generatorul locurile
 *  să ocolească. Camerele cu rol de coridor de evacuare NU: ele sunt derivate
 *  din locuri (`evacuare/pas.py`), nu obstacole pentru ele. Aleile vin din
 *  starea de lucru, ca o alee lungită să se vadă în conflicte și deservire. */
function obstacolele(nivel: StoreyPlan, plan: SolvedPlan, alei: readonly Alee[]): Obstacole {
  const fixe: Obstacol[] = [];
  const inel = (fel: Obstacol["fel"], poly: number[][] | undefined) => {
    if ((poly?.length ?? 0) >= 3) fixe.push({ fel, inel: poly as Pt[] });
  };
  for (const c of nivel.columns) inel("stalp", c.poly);
  for (const w of nivel.walls) inel("perete", w.poly);
  for (const r of nivel.spaces) if (r.kind !== "coridor_evacuare") inel("camera", r.poly);
  for (const r of nivel.cores) inel("camera", r.poly);
  for (const r of nivel.ramps) inel("rampa", r.poly);
  for (const a of nivel.aisles) inel("culoar", a.poly);
  for (const r of plan.accessRamps ?? []) inel("rampa", r.poly);
  return pregatesteObstacole({ contur: (nivel.outline ?? []) as Pt[], alei, fixe });
}

/** Aleile planșei, cu id — cele fără id (documente vechi) nu se pot scrie și
 *  nu intră pe masă ca editabile, dar rămân obstacole. */
const aleileDin = (plan: SolvedPlan): Alee[] =>
  plan.lanes.map((l, i) => ({ ...l, eid: l.eid ?? `__alee${i}` }));

export function useEditorParcaj(o: OptiuniEditorParcaj) {
  const t = useT();
  const { activ, plan, nivel, doc, origine, projectId, solutionId, potScrie, punctul,
          pxPerM, laSalvat, laInchide } = o;
  const [stare, setStare] = useState<Stare>(GOALA);
  const [initial, setInitial] = useState<Stare>(GOALA);
  const { locuri, alei } = stare;
  /** Locurile ALESE, în ordinea alegerii; ultimul e cel «principal» (bara,
   *  mânerul de rotire, axa pe care alunecă grupul). ⇧+clic adaugă sau scoate
   *  un loc; mutarea, rotirea, săgețile și ștergerea merg pe toate. */
  const [alese, setAlese] = useState<string[]>([]);
  const ales = alese.length ? alese[alese.length - 1] : null;
  const setAles = useCallback((id: string | null) => setAlese(id ? [id] : []), []);
  /** ALEEA aleasă (una singură); exclude locurile alese și invers. */
  const [aleeAleasa, setAleeAleasa] = useState<string | null>(null);
  const [unealta, setUnealta] = useState<Unealta>("sageata");
  /** MUTARE LIBERĂ, nu pe axa rândului (Oana, 22.10.2026: «pentru locuri care
   *  sunt puse greșit față de alei va trebui să pot să mut la liber»). Mod din
   *  bară, ca să nu depindă de o tastă ținută apăsat; ⇧ rămâne scurtătura. */
  const [liber, setLiber] = useState(false);
  const [fantoma, setFantoma] = useState<Loc | null>(null);
  /** SE SALVEAZĂ: cât ține scrierea, apoi recitirea documentului (Oana,
   *  22.10.2026: «să apară un loading când se scrie, că durează»). `scriu` e
   *  cererea; `recitesc` ține de la răspuns până vine planul nou — pe un
   *  subsol mare, recitirea e partea lungă. */
  const [lucrez, setLucrez] = useState(false);
  const [recitesc, setRecitesc] = useState(false);
  const [eroare, setEroare] = useState("");
  const [raport, setRaport] = useState("");
  const istoric = useRef(new Istoric<Stare>());
  const tragere = useRef<Tragere | null>(null);
  const stareRef = useRef(stare);
  stareRef.current = stare;

  // Deschiderea: locurile și aleile planșeului, de fiecare dată când se
  // schimbă planul (documentul recitit după salvare) sau pornește modul.
  // Istoricul începe de la zero: ce e pe ecran e ce e în soluție.
  const cheie = activ && plan ? plan.storey : null;
  useEffect(() => {
    const s: Stare = activ && plan
      ? { locuri: plan.spaces.map((x) => ({ ...x })), alei: aleileDin(plan) } : GOALA;
    setStare(s); setInitial(s); setAlese([]); setAleeAleasa(null);
    setUnealta("sageata"); setFantoma(null); setRecitesc(false);
    setEroare(""); istoric.current.goleste(); tragere.current = null;
  }, [activ, plan]);
  useEffect(() => { setRaport(""); }, [cheie]);
  // Plasa recitirii: dacă planul nou nu vine (cererea a picat, documentul n-a
  // ieșit altfel), starea «se recitește» se stinge oricum, ca bara să nu
  // rămână cu uneltele blocate.
  useEffect(() => {
    if (!recitesc) return;
    const id = setTimeout(() => setRecitesc(false), RECITIRE_MAX_MS);
    return () => clearTimeout(id);
  }, [recitesc]);

  const obstacole = useMemo(
    () => (activ && nivel && plan ? obstacolele(nivel, plan, alei) : null),
    [activ, nivel, plan, alei]);
  const originale = useMemo(() => new Map<string, CanonicalObject>(
    (doc?.objects ?? []).filter((x) => x.type === "ParkingSpace" || x.type === "Lane")
      .map((x) => [x.id, x])),
  [doc]);

  const dif = useMemo(() => diferenta(initial.locuri, locuri), [initial, locuri]);
  const difAlei = useMemo(() => diferentaAlei(initial.alei, alei), [initial, alei]);
  const aleiScoase = useMemo(() => aleiSterse(initial.alei, alei), [initial, alei]);
  const nesalvat = activ
    && (dif.schimbate.length + dif.noi.length + dif.sterse.length + difAlei.length
        + aleiScoase.length > 0);

  /** Conflictele locurilor ATINSE, pe id: cele mutate sau noi, plus cele peste
   *  care a ajuns o alee lungită. */
  const conflicte = useMemo(() => {
    const out = new Map<string, Conflict>();
    if (!obstacole) return out;
    const atinse = new Map<string, Loc>();
    for (const l of [...dif.schimbate, ...dif.noi]) atinse.set(l.id, l);
    for (const l of atinse.values()) out.set(l.id, conflicteLe(l, locuri, obstacole));
    // Locurile atinse PRINTR-O ALEE (lungită, rotită, ștearsă) nu s-au mișcat:
    // se întreabă doar ce ține de alee — stau peste ea, au rămas fără ea.
    // Restul suprapunerilor lor sunt ale generatorului, în toleranța lui
    // (`_Podea.incape`), și ar bloca salvarea pe un loc pe care nu l-a atins
    // nimeni: pe 54f276d6, ștergerea unei alei aducea 4 locuri lipite de stâlpi.
    const prinAlee = new Map<string, Loc>();
    for (const a of difAlei) {
      const corp = inelAlee(a);
      for (const l of locuri) if (!atinse.has(l.id) && seSuprapun(inelLoc(l, 0.01), corp)) prinAlee.set(l.id, l);
    }
    // O alee ștearsă: locurile care intrau din ea — cele din fâșia ei, cât
    // ajunge sonda deservirii — se reverifică, ca rămase fără alee să se vadă
    // în chihlimbar înainte de salvare.
    for (const a of aleiScoase) {
      const fasie = inelAlee({ ...a, w: (a.w ?? 5) + 2 * (GOL_DESERVIT + ADANCIME_PROBA) });
      for (const l of locuri) if (!atinse.has(l.id) && seSuprapun(inelLoc(l), fasie)) prinAlee.set(l.id, l);
    }
    for (const l of prinAlee.values()) {
      const c = conflicteLe(l, locuri, obstacole);
      out.set(l.id, { ...c, suprapune: c.suprapune.filter((f) => f === "alee") });
    }
    return out;
  }, [dif, difAlei, aleiScoase, locuri, obstacole]);
  const cuSuprapuneri = [...conflicte.values()].filter((c) => c.suprapune.length).length;

  // ---- schimbări + istoric -------------------------------------------------
  const pune = useCallback((nou: Stare) => { stareRef.current = nou; setStare(nou); }, []);
  const aplica = useCallback((f: (s: Stare) => Stare) => {
    const curent = stareRef.current;
    const nou = f(curent);
    if (nou === curent) return;
    istoric.current.pune(curent);
    pune(nou);
  }, [pune]);
  const peLocuri = useCallback((f: (l: Loc[]) => Loc[]) =>
    aplica((s) => { const l = f(s.locuri); return l === s.locuri ? s : { ...s, locuri: l }; }),
  [aplica]);
  const anuleaza = useCallback(() => {
    const d = istoric.current.anuleaza(stareRef.current);
    if (d) pune(d);
  }, [pune]);
  const refa = useCallback(() => {
    const d = istoric.current.refa(stareRef.current);
    if (d) pune(d);
  }, [pune]);
  const sterge = useCallback(() => {
    if (!alese.length) return;
    const de = new Set(alese);
    peLocuri((l) => l.filter((x) => !de.has(x.id)));
    setAlese([]);
  }, [alese, peLocuri]);
  const rotesteAlesul = useCallback(() => {
    if (!alese.length) return;
    const de = new Set(alese);
    // un singur loc se și lipește de vecini după întoarcere; un grup nu —
    // vecinii lui sunt chiar celelalte din grup, întoarse și ele
    peLocuri((l) => l.map((x) => (de.has(x.id)
      ? (de.size === 1 ? lipeste(roteste(x), l) : roteste(x)) : x)));
  }, [alese, peLocuri]);
  /** Unghiul scris în bară: un pas de istoric, fără lipire — cine scrie 3°
   *  vrea 3°, nu ce-i dă vecinul. */
  const unghiAles = useCallback((deg: number) => {
    if (!alese.length || !Number.isFinite(deg)) return;
    const de = new Set(alese);
    peLocuri((l) => l.map((x) => (de.has(x.id) ? rotesteLa(x, deg) : x)));
  }, [alese, peLocuri]);
  const unghiAlee = useCallback((deg: number) => {
    if (!aleeAleasa || !Number.isFinite(deg)) return;
    aplica((s) => ({ ...s, alei: s.alei.map((a) => (a.eid === aleeAleasa ? rotesteAleea(a, deg) : a)) }));
  }, [aleeAleasa, aplica]);
  /** ȘTERGEREA ALEII alese (Oana, 22.10.2026: «dacă vreau să prelungesc alta
   *  care are sens mai mult să fie prelungită»). Documentul n-are nicio
   *  legătură spre o alee, deci nu rămâne nimic atârnat; locurile care
   *  intrau doar din ea ies «nedeservit». */
  const stergeAleea = useCallback(() => {
    if (!aleeAleasa) return;
    aplica((s) => ({ ...s, alei: s.alei.filter((a) => a.eid !== aleeAleasa) }));
    setAleeAleasa(null);
  }, [aleeAleasa, aplica]);
  const pasAles = useCallback((du: number, dv: number) => {
    if (!alese.length) return;
    const de = new Set(alese);
    peLocuri((l) => l.map((x) => (de.has(x.id) ? pas(x, du, dv) : x)));
  }, [alese, peLocuri]);

  // ---- gesturile pe pânză --------------------------------------------------
  const indexAles = ales ? locuri.findIndex((l) => l.id === ales) : -1;
  const indexAlee = aleeAleasa ? alei.findIndex((a) => a.eid === aleeAleasa) : -1;
  const selectie: Selection | null = !plan ? null
    : indexAles >= 0 ? { kind: "stall", level: plan.level, i: indexAles }
    : indexAlee >= 0 ? { kind: "lane", level: plan.level, i: indexAlee }
    : null;

  /** Apăsarea: pe un loc, un mâner sau un capăt de alee, pornește tragerea.
   *  `true` = luată. */
  const apasa = useCallback((e: PointerEvent<SVGSVGElement>): boolean => {
    if (!activ || !potScrie || unealta !== "sageata") return false;
    const p = punctul(e);
    if (!p) return false;
    const el = e.target instanceof Element ? e.target : null;
    // mânerul de rotire al locului ales
    const idManer = el?.closest("[data-parcaj-maner]")?.getAttribute("data-parcaj-maner");
    if (idManer && stareRef.current.locuri.some((l) => l.id === idManer)) {
      tragere.current = { fel: "roteste", id: idManer, ultim: [p.x, p.y], miscat: false,
                          liber: false, ids: alese.length ? alese : [idManer],
                          start: [p.x, p.y], axa: null };
      return true;
    }
    // mânerul de rotire al aleii alese
    const idAlee = el?.closest("[data-parcaj-maner-alee]")?.getAttribute("data-parcaj-maner-alee");
    if (idAlee && stareRef.current.alei.some((a) => a.eid === idAlee)) {
      tragere.current = { fel: "rotesteAlee", id: idAlee, ultim: [p.x, p.y], miscat: false };
      return true;
    }
    // un capăt al aleii alese
    const capat = el?.closest("[data-parcaj-capat]")?.getAttribute("data-parcaj-capat");
    if (capat) {
      const [eid, c] = capat.split("|");
      if (stareRef.current.alei.some((a) => a.eid === eid)) {
        tragere.current = { fel: "capat", id: eid, capat: c === "1" ? 1 : 0,
                            ultim: [p.x, p.y], miscat: false };
        return true;
      }
    }
    const s = selectionFromTarget(e.target);
    if (!s || s.kind !== "stall") return false;
    const l = stareRef.current.locuri[s.i];
    if (!l) return false;
    // Un loc deja ales trage tot grupul; unul nou îl înlocuiește — sau, cu ⇧,
    // se adaugă la grup și pleacă cu el. Alegerea propriu-zisă (toggle) se face
    // la clic, în `clic`, când apăsarea nu s-a mutat.
    const inGrup = alese.includes(l.id);
    const ids = inGrup ? alese : e.shiftKey ? [...alese, l.id] : [l.id];
    if (!inGrup) { setAlese(ids); setAleeAleasa(null); }
    tragere.current = { fel: "muta", id: l.id, ultim: [p.x, p.y], miscat: false,
                        liber: liber || e.shiftKey, ids, start: [p.x, p.y], axa: null };
    return true;
  }, [activ, potScrie, unealta, punctul, liber, alese]);

  /** Mișcarea: trage locul, mânerul sau capătul, sau plimbă fantoma locului
   *  nou. `true` = luată. */
  const misca = useCallback((e: PointerEvent<SVGSVGElement>): boolean => {
    if (!activ) return false;
    const p = punctul(e);
    if (unealta === "nou" && p && plan) {
      // Fantoma e chiar locul care s-ar pune — cu orientarea și lipirea lui —,
      // ca omul să vadă unde cade înainte să apese.
      setFantoma(locNou([p.x, p.y], stareRef.current.locuri, stareRef.current.alei, "fantoma"));
      return false;
    }
    const d = tragere.current;
    if (!d || !p) return false;
    let dx = p.x - d.ultim[0], dy = p.y - d.ultim[1];
    if (!d.miscat) {
      if (Math.hypot(dx, dy) < 0.02) return true;
      // O mutare pe axă așteaptă `PRAG_AXA` de drum ca să vadă încotro merge
      // mâna; până atunci locul stă, apoi pleacă tot drumul deodată.
      if (d.fel === "muta" && !(d.liber || e.shiftKey)) {
        const sx = p.x - d.start[0], sy = p.y - d.start[1];
        if (Math.hypot(sx, sy) < PRAG_AXA) return true;
        const l0 = stareRef.current.locuri.find((l) => l.id === d.id);
        d.axa = l0 ? axaTragerii(l0, sx, sy) : "u";
        dx = sx; dy = sy;
      }
      // primul pas al tragerii intră în istoric; restul e același gest
      istoric.current.pune(stareRef.current);
      d.miscat = true;
    }
    d.ultim = [p.x, p.y];
    const s = stareRef.current;
    if (d.fel === "capat") {
      pune({ ...s, alei: s.alei.map((a) => (a.eid === d.id ? mutaCapatul(a, d.capat, [p.x, p.y]) : a)) });
      return true;
    }
    if (d.fel === "rotesteAlee") {
      // în jurul mijlocului, ca la loc: 1°, cu ⇧ din 15 în 15
      pune({ ...s, alei: s.alei.map((a) => {
        if (a.eid !== d.id) return a;
        const [mx, my] = mijloculAleii(a);
        return rotesteAleea(a, unghiulDinManer(mx, my, [p.x, p.y], e.shiftKey ? 15 : 1));
      }) });
      return true;
    }
    const de = new Set(d.ids);
    const tras = s.locuri.find((l) => l.id === d.id);
    let locuriNoi: Loc[];
    if (d.fel === "roteste") {
      // ⇧ rotunjește la 15°: un loc «cam strâmb» se îndreaptă dintr-o mișcare.
      // Unghiul e al locului cu mânerul; grupul îl ia pe același, fiecare în
      // jurul centrului lui.
      const deg = tras ? unghiulDinManer(tras.cx, tras.cy, [p.x, p.y], e.shiftKey ? 15 : 1) : 0;
      locuriNoi = s.locuri.map((l) => (de.has(l.id) ? rotesteLa(l, deg) : l));
    } else {
      // Deplasarea se decide pe locul tras (pe axa LUI, dacă nu e liber) și se
      // aplică întreagă, ca translație, la tot grupul.
      const m = tras ? muta(tras, dx, dy, d.liber || e.shiftKey, d.axa ?? "u") : null;
      const ddx = m && tras ? m.cx - tras.cx : dx, ddy = m && tras ? m.cy - tras.cy : dy;
      locuriNoi = s.locuri.map((l) => (de.has(l.id) ? muta(l, ddx, ddy, true) : l));
    }
    pune({ ...s, locuri: locuriNoi });
    return true;
  }, [activ, unealta, plan, punctul, pune]);

  /** Ridicarea: închide tragerea, cu lipirea de vecini — pe unghi și pe
   *  poziție — pentru un singur loc mutat sau rotit. Un grup nu se lipește:
   *  vecinii lui sunt chiar celelalte din grup. `true` = luată. */
  const ridica = useCallback((): boolean => {
    const d = tragere.current;
    tragere.current = null;
    if (!d) return false;
    if (d.miscat && (d.fel === "muta" || d.fel === "roteste") && d.ids.length === 1) {
      const s = stareRef.current;
      pune({ ...s, locuri: s.locuri.map((l, _, toate) => (l.id === d.id ? lipeste(l, toate) : l)) });
    }
    return true;
  }, [pune]);

  /** Un clic (apăsare fără mișcare): pune un loc nou, sau alege un loc ori o alee. */
  const clic = useCallback((e: { clientX: number; clientY: number; shiftKey?: boolean },
                            tinta: EventTarget | null) => {
    if (!activ) return;
    const p = punctul(e);
    if (unealta === "nou") {
      if (!p || !plan || !potScrie) return;
      const id = idCanonic("sp");
      peLocuri((l) => [...l, locNou([p.x, p.y], l, stareRef.current.alei, id)]);
      setAles(id); setAleeAleasa(null);
      return;
    }
    const s = selectionFromTarget(tinta);
    if (s?.kind === "lane") {
      const a = stareRef.current.alei[s.i];
      // aleile fără id în document nu se pot scrie, deci nici alege
      if (a && !a.eid.startsWith("__")) {
        setAlese([]);
        setAleeAleasa((v) => (v === a.eid ? null : a.eid));
        return;
      }
    }
    const l = s?.kind === "stall" ? stareRef.current.locuri[s.i] : null;
    setAleeAleasa(null);
    // ⇧+clic: adaugă la grup sau scoate din el (Oana, 22.10.2026, «cum fac
    // multi select»); clic simplu: doar el, sau nimic pe al doilea clic.
    setAlese((v) => {
      if (!l) return [];
      if (e.shiftKey) return v.includes(l.id) ? v.filter((x) => x !== l.id) : [...v, l.id];
      return v.length === 1 && v[0] === l.id ? [] : [l.id];
    });
  }, [activ, unealta, plan, potScrie, punctul, peLocuri, setAles]);

  // ---- tastele -------------------------------------------------------------
  useEffect(() => {
    if (!activ) return;
    const scrie = (el: EventTarget | null) => {
      const h = el as HTMLElement | null;
      return !!h && (/^(INPUT|TEXTAREA|SELECT)$/.test(h.tagName) || h.isContentEditable);
    };
    const jos = (e: KeyboardEvent) => {
      if (scrie(e.target)) return;
      const k = e.key.toLowerCase();
      const cmd = e.metaKey || e.ctrlKey;
      let luata = true;
      if (cmd && k === "z") { if (e.shiftKey) refa(); else anuleaza(); }
      else if (cmd) luata = false;
      else if (k === "escape") {
        if (unealta === "nou") { setUnealta("sageata"); setFantoma(null); }
        else if (ales) setAlese([]);
        else if (aleeAleasa) setAleeAleasa(null);
        else luata = false;
      }
      else if ((k === "delete" || k === "backspace") && ales && potScrie) sterge();
      else if ((k === "delete" || k === "backspace") && aleeAleasa && potScrie) stergeAleea();
      else if (k === "r" && ales && potScrie) rotesteAlesul();
      else if (k === "n" && potScrie) setUnealta("nou");
      else if (k.startsWith("arrow") && ales && potScrie) {
        const p = e.shiftKey ? PAS_SAGEATA_MARE : PAS_SAGEATA;
        if (k === "arrowleft") pasAles(-p, 0);
        else if (k === "arrowright") pasAles(p, 0);
        else if (k === "arrowup") pasAles(0, -p);
        else pasAles(0, p);
      }
      else luata = false;
      if (luata) { e.preventDefault(); e.stopPropagation(); }
    };
    // În faza de CAPTURĂ, înaintea tastelor pânzei (`useComenziPanZoom`, care
    // panoramează pe săgeți și reîncadrează pe `r`): cât e un loc ales,
    // tastele sunt ale lui.
    window.addEventListener("keydown", jos, true);
    return () => window.removeEventListener("keydown", jos, true);
  }, [activ, ales, aleeAleasa, unealta, potScrie, anuleaza, refa, sterge, stergeAleea,
      rotesteAlesul, pasAles]);

  // ---- garda nescrisului ---------------------------------------------------
  useEffect(() => {
    if (!nesalvat) return;
    const la = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener("beforeunload", la);
    tineNescrisul(() => window.confirm(tr("parcaj.pleci_nesalvat")));
    return () => {
      window.removeEventListener("beforeunload", la);
      tineNescrisul(null);
    };
  }, [nesalvat]);

  // ---- scrierea ------------------------------------------------------------
  const salveaza = useCallback(async () => {
    if (!doc || !plan || !nesalvat) return;
    if (cuSuprapuneri) { setEroare(t("parcaj.refuz_suprapuneri", { n: cuSuprapuneri })); return; }
    const obiecte = inCadrulDocumentului(
      [...caObiecte([...dif.schimbate, ...dif.noi], originale, plan.storey),
       ...aleiCaObiecte(difAlei, originale)],
      doc.crs, origine);
    setLucrez(true); setEroare("");
    try {
      await api.scrieObiecteSolutie(projectId, solutionId, obiecte,
                                    [...dif.sterse, ...aleiScoase.map((a) => a.eid)]);
      setRaport(t("parcaj.scris", { mutate: dif.schimbate.length, noi: dif.noi.length,
                                    sterse: dif.sterse.length,
                                    alei: difAlei.length + aleiScoase.length }));
      setRecitesc(true);
      laSalvat();
    } catch (e) {
      setEroare(String((e as Error)?.message ?? e));
    } finally {
      setLucrez(false);
    }
  }, [doc, plan, nesalvat, cuSuprapuneri, dif, difAlei, aleiScoase, originale, origine, projectId,
      solutionId, laSalvat, t]);

  // ---- ce se desenează peste planșă ---------------------------------------
  const k = Math.max(pxPerM, 1e-6);
  /** COTELE DE VECINĂTATE ale locului principal ales — și al celui tras, care
   *  e ales cât îl tragi (Oana, 23.10.2026). Doar unul, ca la mai multe locuri
   *  alese planșa să nu se umple de cifre. */
  const cote = useMemo(() => {
    const l = ales ? locuri.find((x) => x.id === ales) : null;
    return l && obstacole ? coteVecinatate(l, locuri, obstacole) : [];
  }, [ales, locuri, obstacole]);
  const aleea = aleeAleasa ? alei.find((a) => a.eid === aleeAleasa) ?? null : null;
  const strat: ReactNode = activ ? (
    <g className="parcaj-editor" pointerEvents="none">
      {[...conflicte.entries()].map(([id, c]) => {
        const l = locuri.find((x) => x.id === id);
        if (!l || (!c.suprapune.length && !c.nedeservit)) return null;
        const rau = c.suprapune.length > 0;
        return (
          <polygon key={id} points={inelLoc(l).map((p) => p.join(",")).join(" ")}
                   fill={rau ? "rgba(200, 40, 40, .25)" : "rgba(220, 150, 20, .25)"}
                   stroke={rau ? "#c42828" : "#d89614"} strokeWidth={2}
                   vectorEffect="non-scaling-stroke" />
        );
      })}
      {/* Celelalte locuri ale grupului ales: conturul de selecție, ca al
          principalului din `PlanView`. */}
      {alese.length > 1 && alese.slice(0, -1).map((id) => {
        const l = locuri.find((x) => x.id === id);
        return l ? (
          <polygon key={`sel-${id}`} points={inelLoc(l).map((p) => p.join(",")).join(" ")}
                   fill="none" stroke="var(--acc, #0b7f92)" strokeWidth={2}
                   vectorEffect="non-scaling-stroke" />
        ) : null;
      })}
      {/* Cotele de vecinătate: cifra cât ~12 px pe ecran, la orice zoom; golul
          din față până la alee peste `GOL_DESERVIT` iese în chihlimbar — de
          acolo locul nu mai e deservit. Un obiect lipit (0) n-are linie. */}
      {cote.map((c) => (
        // `data-cota`: latura și felul vecinului, citite de probe (și de om, în
        // inspectorul browserului)
        <g key={`cv-${c.latura}`} data-cota={`${c.latura}:${c.fel}`}>
          <Cota fs={Math.min(1.2, Math.max(0.25, 12 / k))}
                culoare={c.fel === "alee" && c.m > GOL_DESERVIT ? "#b26b00" : COTA_INT}
                d={{ x1: c.a[0], y1: c.a[1], x2: c.b[0], y2: c.b[1],
                     label: c.m.toFixed(2).replace(".", ",") }} />
        </g>
      ))}
      {/* MÂNERUL DE ROTIRE al locului ales: un cerc dincolo de fața locului, legat
          de ea cu o linie; tras în jurul centrului, dă unghiul (Oana, 22.10.2026:
          «vreau să pot roti la liber locul, nu doar la 90°»). Mărime fixă pe
          ecran, prin scară. Primește apăsări, spre deosebire de restul stratului. */}
      {unealta === "sageata" && potScrie && (() => {
        const l = ales ? locuri.find((x) => x.id === ales) : null;
        if (!l) return null;
        const [mx, my] = pozitiaManerului(l, MANER_PX / k);
        const [fx, fy] = pozitiaManerului(l, 0);
        return (
          <g pointerEvents="all" data-parcaj-maner={l.id} style={{ cursor: "grab" }}>
            <line x1={fx} y1={fy} x2={mx} y2={my} stroke="var(--acc, #0b7f92)"
                  strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
            <circle cx={mx} cy={my} r={MANER_RAZA_PX / k} fill="#fff"
                    stroke="var(--acc, #0b7f92)" strokeWidth={2} vectorEffect="non-scaling-stroke" />
          </g>
        );
      })()}
      {/* CAPETELE ALEII alese: două cercuri, trase pe direcția ei (Oana,
          22.10.2026: «să pot să lungesc/scurtez»). */}
      {unealta === "sageata" && potScrie && aleea && ([[aleea.x1, aleea.y1, 0], [aleea.x2, aleea.y2, 1]] as const)
        .map(([x, y, c]) => (
          <g key={c} pointerEvents="all" data-parcaj-capat={`${aleea.eid}|${c}`}
             style={{ cursor: "move" }}>
            <circle cx={x} cy={y} r={(MANER_RAZA_PX + 2) / k} fill="#fff"
                    stroke="var(--acc, #0b7f92)" strokeWidth={2} vectorEffect="non-scaling-stroke" />
          </g>
        ))}
      {/* MÂNERUL DE ROTIRE al aleii: perpendicular pe mijlocul ei, tras în jurul
          mijlocului; lungimea rămâne. */}
      {unealta === "sageata" && potScrie && aleea && (() => {
        const [mx, my] = mijloculAleii(aleea);
        const [hx, hy] = manerulAleii(aleea, MANER_PX / k);
        const [fx, fy] = manerulAleii(aleea, 0);
        return (
          <g pointerEvents="all" data-parcaj-maner-alee={aleea.eid} style={{ cursor: "grab" }}>
            <line x1={fx} y1={fy} x2={hx} y2={hy} stroke="var(--acc, #0b7f92)"
                  strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
            <circle cx={mx} cy={my} r={3 / k} fill="var(--acc, #0b7f92)" />
            <circle cx={hx} cy={hy} r={MANER_RAZA_PX / k} fill="#fff"
                    stroke="var(--acc, #0b7f92)" strokeWidth={2} vectorEffect="non-scaling-stroke" />
          </g>
        );
      })()}
      {unealta === "nou" && fantoma && (
        <polygon points={inelLoc(fantoma).map((p) => p.join(",")).join(" ")}
                 fill="rgba(11, 127, 146, .18)" stroke="var(--acc, #0b7f92)" strokeWidth={1.5}
                 strokeDasharray="4 3" vectorEffect="non-scaling-stroke" />
      )}
    </g>
  ) : null;

  // ---- bara ---------------------------------------------------------------
  const alesul = ales ? locuri.find((l) => l.id === ales) ?? null : null;
  const conflictAles = ales ? conflicte.get(ales) ?? null : null;
  const salvez = lucrez || recitesc;
  const bara: ReactNode = activ && plan ? (
    <div className={`card bara-parcaj${salvez ? " salvez" : ""}`}>
      <div className="bara-parcaj-rand">
        <b className="ob-ed-nume">{t("parcaj.titlu")}</b>
        <span className="ob-ed-sub">{nivel?.label ?? plan.label}</span>
        <span className="ob-ed-sp" />
        <span className="ob-ed-stare">
          {salvez ? (
            <span className="pg-recomp-lucru"><span className="pg-recomp-spin" aria-hidden="true" />
              {lucrez ? t("comun.se_salveaza") : t("parcaj.se_reciteste")}</span>
          ) : nesalvat ? t("editor.nesalvat") : t("editor.salvat")}
        </span>
        <button className="btn" onClick={laInchide} disabled={salvez}>
          {t("nivel.inchide_editarea")}
        </button>
        <button className="btn btnp" onClick={() => { void salveaza(); }}
                title={t("parcaj.salveaza_titlu")}
                disabled={salvez || !potScrie || !nesalvat}>
          {salvez ? t("comun.se_salveaza") : t("nivel.salveaza")}
        </button>
      </div>
      <div className="bara-parcaj-rand">
        <button className={`btn${unealta === "sageata" ? " on" : ""}`}
                onClick={() => { setUnealta("sageata"); setFantoma(null); }}
                title={t("parcaj.unealta.sageata_titlu")}>
          {t("parcaj.unealta.sageata")}
        </button>
        <button className={`btn${unealta === "nou" ? " on" : ""}`} disabled={!potScrie}
                onClick={() => { setUnealta("nou"); setAles(null); setAleeAleasa(null); }}
                title={t("parcaj.unealta.nou_titlu")}>
          {t("parcaj.unealta.nou")}
        </button>
        <button className={`btn${liber ? " on" : ""}`} disabled={!potScrie}
                onClick={() => setLiber((v) => !v)} title={t("parcaj.liber_titlu")}>
          {t("parcaj.liber")}
        </button>
        <button className="btn" onClick={anuleaza} disabled={!istoric.current.poateAnula}
                title="⌘Z">↶</button>
        <button className="btn" onClick={refa} disabled={!istoric.current.poateReface}
                title="⇧⌘Z">↷</button>
        <span className="ob-ed-sp" />
        {alesul ? (
          <>
            <span className="ob-ed-sub">
              {alese.length > 1
                ? t("parcaj.alese", { n: alese.length })
                : t("parcaj.ales", { n: alesul.n, fel: alesul.kind === "ada" ? "PMR" : "std",
                                     w: alesul.w.toFixed(2), d: alesul.d.toFixed(2) })}
            </span>
            <label className="ob-ed-sub" title={t("parcaj.unghi_titlu")}>
              {t("parcaj.unghi")}{" "}
              <CampUnghi key={alesul.id} deg={alesul.deg} disabled={!potScrie}
                         laScris={unghiAles} />°
            </label>
            <button className="btn" onClick={rotesteAlesul} disabled={!potScrie}
                    title={t("parcaj.roteste_titlu")}>{t("parcaj.roteste")}</button>
            <button className="btn" onClick={sterge} disabled={!potScrie}
                    title={t("parcaj.sterge_titlu")}>{t("parcaj.sterge")}</button>
          </>
        ) : aleea ? (
          <>
            <span className="ob-ed-sub">
              {t("parcaj.aleea", { l: lungimeaAleii(aleea).toFixed(2), w: (aleea.w ?? 5).toFixed(2) })}
            </span>
            <label className="ob-ed-sub" title={t("parcaj.unghi_alee_titlu")}>
              {t("parcaj.unghi")}{" "}
              <CampUnghi key={aleea.eid} deg={unghiulAleii(aleea)} disabled={!potScrie}
                         laScris={unghiAlee} />°
            </label>
            <button className="btn" onClick={stergeAleea} disabled={!potScrie}
                    title={t("parcaj.sterge_aleea_titlu")}>{t("parcaj.sterge")}</button>
          </>
        ) : (
          <span className="ob-ed-sub">
            {unealta === "nou" ? t("parcaj.sfat_nou") : t("parcaj.sfat")}
          </span>
        )}
      </div>
      {(conflictAles && (conflictAles.suprapune.length || conflictAles.nedeservit)) ? (
        <div className={`bara-parcaj-rand ${conflictAles.suprapune.length ? "err" : "amb"}`}>
          {conflictAles.suprapune.length
            ? t("parcaj.peste", { ce: conflictAles.suprapune
                .map((f) => t(`parcaj.fel.${f}` as "parcaj.fel.loc")).join(", ") })
            : t("parcaj.nedeservit")}
        </div>
      ) : null}
      {(eroare || raport) && (
        <div className={`bara-parcaj-rand ${eroare ? "err" : ""}`}>{eroare || raport}</div>
      )}
    </div>
  ) : null;

  return { activ, locuri, alei, selectie, nesalvat, apasa, misca, ridica, clic, strat, bara,
           alese, unealta, storey: plan?.storey ?? null };
}

/** Câmpul cu unghiul locului ales. Ține textul cât se scrie și îl trimite la
 *  Enter sau la ieșirea din câmp — nu la fiecare tastă, ca să nu fie un pas de
 *  istoric pe fiecare cifră. `key={id}` îl reface la alt loc. */
function CampUnghi({ deg, disabled, laScris }: {
  deg: number; disabled: boolean; laScris: (deg: number) => void;
}) {
  const afisat = (Math.round(deg * 10) / 10).toString();
  const [text, setText] = useState(afisat);
  const [scriu, setScriu] = useState(false);
  const trimite = () => {
    setScriu(false);
    const v = Number(text.replace(",", "."));
    if (Number.isFinite(v) && Math.abs(v - deg) > 1e-9) laScris(v);
  };
  return (
    <input className="parcaj-unghi" type="text" inputMode="decimal" disabled={disabled}
           value={scriu ? text : afisat}
           onFocus={() => { setText(afisat); setScriu(true); }}
           onChange={(e) => setText(e.target.value)}
           onBlur={trimite}
           onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                               if (e.key === "Escape") { setScriu(false); (e.target as HTMLInputElement).blur(); } }} />
  );
}
